import { ChevronLeft, ChevronRight, ImageOff, Play } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { miniaturaDoProduto } from "../../utils/miniatura";

import { podeTocarVideo, urlMidia } from "./midiasPublicas";

// Miniatura de 128 px (o servidor gera e guarda); foto que não é de produto vai inteira
const fotoMini = (url) => miniaturaDoProduto(url, 128) || urlMidia(url);

/**
 * As fotos e o vídeo do produto no detalhe (cardápio, Mesa QR, Totem): arrasta
 * para o lado (ou setas no computador), em loop — da 3ª volta
 * para a 1ª (pedido do dono, 23/10/2026).
 *
 * Embaixo, as miniaturas das outras mídias (as duas seguintes) no lugar das
 * bolinhas: tocar numa leva para ela, e elas trocam conforme navega (pedido do
 * dono, 24/10/2026). Miniatura de 128 px, então não pesa o carregamento.
 *
 * Carregamento: abre baixando só a mídia da vez; quando ela termina, a próxima
 * é pré-carregada (para o arrasto ser instantâneo). O que já foi visto fica.
 * O vídeo usa a capa enquanto carrega e só toca quando está na tela; trocar de
 * mídia pausa.
 */
export default function CarrosselMidias({ midias, alt = "", fit = "object-contain", escuro = false }) {
  const [i, setI] = useState(0);
  const [erro, setErro] = useState({});
  const toque = useRef(null);
  const videos = useRef({});
  const n = midias.length;
  const ir = (d) => setI((x) => (x + d + n) % n);
  const tocar = podeTocarVideo();
  const [liberados, setLiberados] = useState(() => new Set([0]));
  const liberar = (k) => setLiberados((s) => (s.has(k) ? s : new Set(s).add(k)));
  useEffect(() => { liberar(i); }, [i]);
  const carregou = (k) => { if (k === i && n > 1) liberar((i + 1) % n); };

  useEffect(() => {
    Object.entries(videos.current).forEach(([k, v]) => {
      if (!v) return;
      if (Number(k) === i && tocar) v.play().catch(() => {});
      else v.pause();
    });
  }, [i, tocar]);

  const inicio = (e) => { toque.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }; };
  const fim = (e) => {
    if (!toque.current) return;
    const dx = e.changedTouches[0].clientX - toque.current.x;
    const dy = e.changedTouches[0].clientY - toque.current.y;
    toque.current = null;
    if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) ir(dx < 0 ? 1 : -1);
  };

  return (
    <div className="relative w-full h-full overflow-hidden select-none" onTouchStart={inicio} onTouchEnd={fim} data-e2e="carrossel-midias">
      <div className="flex h-full transition-transform duration-300 ease-out" style={{ transform: `translateX(-${i * 100}%)` }}>
        {midias.map((m, k) => {
          const perto = liberados.has(k);
          return (
            <div key={k} className="w-full h-full shrink-0 grid place-items-center">
              {erro[k] ? (
                <ImageOff className={`w-12 h-12 ${escuro ? "text-zinc-600" : "text-zinc-300"}`} />
              ) : m.tipo === "video" ? (
                <video ref={(el) => { videos.current[k] = el; }} src={perto ? urlMidia(m.url) : undefined} poster={urlMidia(m.poster)}
                  muted loop playsInline preload={k === i ? "auto" : "none"} className={`w-full h-full ${fit}`}
                  onLoadedData={() => carregou(k)}
                  onError={() => setErro((x) => ({ ...x, [k]: true }))} />
              ) : (
                <img src={perto ? urlMidia(m.url) : undefined} alt={k === 0 ? alt : ""} loading={k === 0 ? "eager" : "lazy"} decoding="async"
                  className={`w-full h-full ${fit}`} onLoad={() => carregou(k)} onError={() => setErro((x) => ({ ...x, [k]: true }))} />
              )}
            </div>
          );
        })}
      </div>
      {n > 1 && (
        <>
          <button type="button" onClick={() => ir(-1)} aria-label="Anterior"
            className="hidden sm:grid absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 place-items-center rounded-full bg-black/35 hover:bg-black/55 text-white">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button type="button" onClick={() => ir(1)} aria-label="Próxima"
            className="hidden sm:grid absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 place-items-center rounded-full bg-black/35 hover:bg-black/55 text-white">
            <ChevronRight className="w-5 h-5" />
          </button>
          <div className="absolute bottom-2.5 left-1/2 -translate-x-1/2 flex items-center gap-2" data-e2e="carrossel-miniaturas">
            {[1, 2].filter((d) => d < n).map((d) => {
              const k = (i + d) % n;
              const m = midias[k];
              return (
                <button key={k} type="button" onClick={() => setI(k)} aria-label={`Ver ${m.tipo === "video" ? "o vídeo" : `a foto ${k + 1}`}`}
                  className="relative w-12 h-12 sm:w-14 sm:h-14 rounded-xl overflow-hidden ring-2 ring-white/90 shadow-lg bg-black/20 active:scale-95 transition-transform">
                  <img src={fotoMini(m.tipo === "video" ? m.poster : m.url)} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                  {m.tipo === "video" && (
                    <span className="absolute inset-0 grid place-items-center bg-black/25 text-white"><Play className="w-4 h-4 ml-0.5" fill="currentColor" /></span>
                  )}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
